import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Trash2, AlertTriangle } from 'lucide-react';
import { NODE_META, NODE_HANDLES, COLOR_CLASSES } from './nodeTypes';

/* Per-source-handle accent colors. Mirrors edgeDecorator.js so the
   handle dot, the wire, and the label chip all share one color and
   the eye can follow a single branch across the canvas. */
const HANDLE_DOT_HEX = {
  out: '#2dd4bf', open: '#34d399', closed: '#fbbf24',
  has: '#34d399', lacks: '#94a3b8', known: '#34d399', unknown: '#94a3b8',
  no_answer: '#fb7185', timeout: '#fbbf24',
};
function dotHexFor(handleId) {
  if (HANDLE_DOT_HEX[handleId]) return HANDLE_DOT_HEX[handleId];
  if (handleId && handleId.startsWith('key_')) return '#38bdf8';
  return '#2dd4bf';
}

/* Custom React Flow node renderer.
   Layout (top → bottom):
     1. Header bar (icon + label)
     2. Subtitle (one-line summary of the config)
     3. Output handle rows — each row's Handle anchors at Position.Right
        so the edge always exits cleanly from the right edge of the node
        (never crossing through the body).
   - Top input handle on every non-start node.
   - Hover delete (×) badge in the top-right (except start). */
function CallFlowNode({ data, selected, type, id }) {
  const [hover, setHover] = useState(false);
  const meta = NODE_META[type] || { label: type, icon: () => null, color: 'sky' };
  const Icon = meta.icon;
  const c = COLOR_CLASSES[meta.color] || COLOR_CLASSES.sky;
  const handlesDef = NODE_HANDLES[type];
  const outputs = typeof handlesDef === 'function' ? handlesDef({ data }) : handlesDef || [];
  const hasInput = type !== 'start';
  const subtitle = renderSubtitle(type, data);

  // Live-test pulse glow ring (parent injects data._pulse on real-call traversal)
  const pulse = data?._pulse;
  const pulseRing =
    pulse === 'matched' || pulse === 'ok' || pulse === 'prompt' ? 'shadow-[0_0_0_3px_rgba(52,211,153,0.55),0_0_24px_rgba(52,211,153,0.6)]'
    : pulse === 'fallback' || pulse === 'no_answer' || pulse === 'timeout' ? 'shadow-[0_0_0_3px_rgba(251,191,36,0.55),0_0_24px_rgba(251,191,36,0.6)]'
    : pulse === 'visited' ? 'shadow-[0_0_0_2px_rgba(45,212,191,0.30)]'
    : pulse === 'skipped' ? 'shadow-[0_0_0_3px_rgba(148,163,184,0.45),0_0_18px_rgba(148,163,184,0.45)]'
    : pulse === 'error' ? 'shadow-[0_0_0_3px_rgba(244,63,94,0.55),0_0_24px_rgba(244,63,94,0.6)]'
    : '';

  const onDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (typeof data?._onDelete === 'function') data._onDelete(id);
  };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`relative min-w-[230px] max-w-[260px] rounded-xl border transition-all duration-300 ${selected ? 'border-teal-400 shadow-[0_0_0_2px_rgba(45,212,191,0.35)]' : ''} ${pulseRing}`}
      style={{
        background: 'var(--vox-node-bg)',
        borderColor: selected ? '#2dd4bf' : 'var(--vox-node-border)',
      }}
      data-testid={`callflow-node-${type}-${id}`}
    >
      {/* Top input handle — anchored to the top center edge */}
      {hasInput && (
        <Handle
          type="target"
          position={Position.Top}
          id="in"
          className="!border-0 !w-2.5 !h-2.5"
          style={{ background: 'rgba(148,163,184,0.55)' }}
        />
      )}

      {/* Hover delete (× badge) — never on the start node */}
      {type !== 'start' && hover && (
        <button
          onClick={onDeleteClick}
          className="absolute -top-2 -right-2 z-20 w-6 h-6 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-lg border-2"
          style={{ borderColor: 'var(--vox-handle-ring)' }}
          title="Delete node"
          data-testid={`callflow-node-delete-${id}`}
        >
          <Trash2 size={11} />
        </button>
      )}

      {/* Header */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-t-xl ${c.bg}`}>
        <div className={`w-7 h-7 rounded-lg ${c.bg} ${c.text} flex items-center justify-center border ${c.border}`}>
          {Icon ? <Icon size={14} /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[11px] font-bold uppercase tracking-wider ${c.text}`}>{meta.label}</div>
          {data?.label && <div className="text-xs truncate" style={{ color: 'var(--vox-text-primary)' }}>{data.label}</div>}
        </div>
      </div>

      {/* Subtitle */}
      {subtitle && (
        <div
          className="px-3 py-2 text-[11px] whitespace-pre-line border-t"
          style={{
            color: 'var(--vox-text-secondary)',
            borderColor: 'var(--vox-node-border)',
          }}
        >
          {subtitle}
        </div>
      )}

      {/* Bottom output strip — Twilio-Studio style. Each output gets a
          handle dot + label at the bottom edge of the node, evenly
          distributed horizontally. Edges exit downward → clean
          top-to-bottom flow without long sideways detours. */}
      {outputs.length > 0 && (
        <div
          className="border-t px-3 pt-2.5 pb-3 flex items-stretch justify-around gap-2 relative"
          style={{ borderColor: 'var(--vox-node-border)' }}
        >
          {outputs.map((h, idx) => {
            const dotHex = dotHexFor(h.id);
            const unwired = data?._unwiredHandles instanceof Set && data._unwiredHandles.has(h.id);
            // Distribute handles evenly along the bottom edge
            const leftPct = ((idx + 0.5) / outputs.length) * 100;
            return (
              <React.Fragment key={h.id}>
                <div
                  className="flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5"
                  title={h.label}
                >
                  <div className="flex items-center gap-1 max-w-full">
                    <span
                      className="text-[9px] font-bold uppercase tracking-wider truncate"
                      style={{ color: dotHex }}
                    >
                      {h.label}
                    </span>
                    {unwired && (
                      <span
                        title="This branch has no destination — calls hitting this path will hang up."
                        className="inline-flex items-center px-1 rounded text-[7px] font-bold tracking-wider bg-amber-400/15 border border-amber-400/30 text-amber-500 dark:text-amber-300"
                        data-testid={`callflow-unwired-${h.id}`}
                      >
                        <AlertTriangle size={7} />
                      </span>
                    )}
                  </div>
                </div>
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id={h.id}
                  className="!border-2 !w-3 !h-3"
                  style={{
                    backgroundColor: dotHex,
                    borderColor: 'var(--vox-handle-ring)',
                    left: `${leftPct}%`,
                  }}
                />
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Single output handle for nodes with no enumerated outputs (start) */}
      {outputs.length === 0 && type !== 'hangup' && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="out"
          className="!border-2 !w-3 !h-3"
          style={{
            backgroundColor: '#2dd4bf',
            borderColor: 'var(--vox-handle-ring)',
          }}
        />
      )}
    </div>
  );
}

function renderSubtitle(type, data) {
  if (!data) return '';
  switch (type) {
    case 'start':          return data._phoneNumber ? data._phoneNumber : 'Bind a phone number on the Call Routing list';
    case 'menu': {
      const navBits = [];
      if (data.repeat_enabled) navBits.push(`${data.repeat_key || '9'} ↻ repeat`);
      if (data.main_menu_enabled) navBits.push(`${data.main_menu_key || '0'} ↑ main`);
      const prompt = data.prompt ? truncate(data.prompt, 50) : 'No prompt set';
      return navBits.length ? `${prompt}\n${navBits.join(' · ')}` : prompt;
    }
    case 'time_router':    return `${(data.windows || []).length} window(s) · ${data.timezone || 'no TZ'}`;
    case 'tag_filter':     return data.tag ? `Tag: ${data.tag}` : 'No tag set';
    case 'contact_filter': return 'Known vs unknown';
    case 'ai_agent':       return data.agent_name || (data.agent_id ? `Agent ${data.agent_id.slice(0,12)}…` : 'No agent picked');
    case 'transfer':       return data.number ? `${data.number} · ${data.ring_timeout || 20}s ring` : 'No number set';
    case 'hangup':         return data.farewell ? `"${truncate(data.farewell, 50)}"` : 'No farewell';
    default:               return '';
  }
}
function truncate(s, n) { return (s && s.length > n) ? s.slice(0, n - 1) + '…' : (s || ''); }

export default React.memo(CallFlowNode);
